// Preview: separator/divider design options for the hero before/after visual.
const CREAM = "#F7F5F0";
const INK = "#3E2818";
const TERRA = "#D1633D";
const CARAMEL = "#8A5730";
const MONO = "'IBM Plex Mono', monospace";
const POS = "40%";

const Panel = () => (
  <>
    {/* left: traditional (cream) */}
    <div style={{ position: "absolute", inset: 0, background: CREAM, padding: "16px" }}>
      <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "1px", color: "#9CA3AF" }}>TRADITIONAL</div>
      <div style={{ marginTop: 14, height: 10, width: "60%", borderRadius: 4, background: "#E4E1DA" }} />
      <div style={{ marginTop: 10, height: 10, width: "72%", borderRadius: 4, background: "#E4E1DA" }} />
      <div style={{ marginTop: 10, height: 10, width: "50%", borderRadius: 4, background: "#E4E1DA" }} />
    </div>
    {/* right: agentic (espresso), clipped */}
    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg,#211307,#2B1E15)", clipPath: `inset(0 0 0 ${POS})`, padding: "16px", boxSizing: "border-box" }}>
      <div style={{ position: "absolute", left: `calc(${POS} + 16px)`, top: 16, fontFamily: MONO, fontSize: 9, letterSpacing: "1px", color: TERRA }}>AGENTIC</div>
      <div style={{ position: "absolute", left: `calc(${POS} + 16px)`, top: 44, height: 10, width: "40%", borderRadius: 4, background: "rgba(255,255,255,.16)" }} />
      <div style={{ position: "absolute", left: `calc(${POS} + 16px)`, top: 64, height: 10, width: "48%", borderRadius: 4, background: "rgba(255,255,255,.16)" }} />
      <div style={{ position: "absolute", left: `calc(${POS} + 16px)`, top: 84, height: 10, width: "34%", borderRadius: 4, background: "rgba(255,255,255,.16)" }} />
    </div>
  </>
);

const Box = ({ title, note, children }) => (
  <div>
    <p style={{ fontFamily: MONO, fontSize: 11, letterSpacing: "2px", textTransform: "uppercase", color: TERRA, marginBottom: 6 }}>{title}</p>
    <div style={{ position: "relative", height: 200, borderRadius: 14, overflow: "hidden", border: "1px solid rgba(62,40,24,.12)" }}>
      <Panel />
      {children}
    </div>
    <p style={{ fontSize: 13, color: "rgba(62,40,24,.7)", marginTop: 8, lineHeight: 1.5 }}>{note}</p>
  </div>
);

const Handle = ({ bg, fg, ring, pill }) => (
  <div style={{ position: "absolute", top: "50%", left: POS, transform: "translate(-50%,-50%)", width: pill ? 30 : 34, height: 34, borderRadius: 999, background: bg, color: fg, display: "flex", alignItems: "center", justifyContent: "center", gap: 2, fontSize: 12, boxShadow: `0 6px 18px -6px rgba(33,19,7,.5)${ring ? `, 0 0 0 4px ${ring}` : ""}` }}>
    <span>&#8249;</span><span>&#8250;</span>
  </div>
);

export default function SeparatorPreview() {
  return (
    <div style={{ minHeight: "100vh", background: "#E7E1D6", padding: "40px 16px" }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 28, color: INK, marginBottom: 4 }}>Separator — Design Options</h2>
        <p style={{ color: "rgba(62,40,24,.7)", marginBottom: 28 }}>No bright glow. Pick a <b>line style</b> + a <b>handle style</b> (mix &amp; match).</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28 }}>

          <Box title="A · Handle only (no line)" note="Panels meet edge-to-edge. Only the drag handle marks the seam. Cleanest, most modern.">
            <Handle bg="#fff" fg={INK} />
          </Box>

          <Box title="B · Thin terracotta line" note="A crisp 1.5px terracotta line, no glow. Clear seam that stays on-brand.">
            <div style={{ position: "absolute", top: 0, bottom: 0, left: POS, width: 1.5, background: TERRA, transform: "translateX(-0.75px)" }} />
            <Handle bg="#fff" fg={TERRA} />
          </Box>

          <Box title="C · Soft seam (shadow, no line)" note="A gentle shadow where the agentic panel overlaps — reads like a fold. Subtle, no hard line.">
            <div style={{ position: "absolute", top: 0, bottom: 0, left: POS, width: 20, transform: "translateX(-20px)", background: "linear-gradient(90deg, rgba(33,19,7,0), rgba(33,19,7,.16))", pointerEvents: "none" }} />
            <Handle bg="linear-gradient(135deg,#211307,#2B1E15)" fg="#fff" />
          </Box>

          <Box title="D · Caramel hairline" note="A quiet 1px caramel hairline. Understated and warm — blends into the palette.">
            <div style={{ position: "absolute", top: 0, bottom: 0, left: POS, width: 1, background: CARAMEL, opacity: 0.8, transform: "translateX(-0.5px)" }} />
            <Handle bg={CREAM} fg={CARAMEL} />
          </Box>

          <Box title="E · Dashed line" note="A thin dashed espresso line — hints at a 'cut' between the two states.">
            <div style={{ position: "absolute", top: 8, bottom: 8, left: POS, width: 0, borderLeft: `1.5px dashed rgba(62,40,24,.45)`, transform: "translateX(-0.75px)" }} />
            <Handle bg="#fff" fg={INK} />
          </Box>

          <Box title="F · Cream line + soft ring handle" note="A 2px cream line with a subtle (not bright) terracotta ring on the handle. Refined middle-ground.">
            <div style={{ position: "absolute", top: 0, bottom: 0, left: POS, width: 2, background: "rgba(247,245,240,.9)", transform: "translateX(-1px)" }} />
            <Handle bg="#fff" fg={INK} ring="rgba(198,93,59,.25)" />
          </Box>

        </div>

        <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 22, color: INK, margin: "44px 0 4px" }}>With glow effect</h3>
        <p style={{ color: "rgba(62,40,24,.7)", marginBottom: 24 }}>Softer, on-brand glows (none as harsh as the original).</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28 }}>

          <Box title="G · Soft terracotta glow" note="2px cream line with a gentle terracotta halo. Warm, subtle — reads premium.">
            <div style={{ position: "absolute", top: 0, bottom: 0, left: POS, width: 2, background: "rgba(247,245,240,.95)", transform: "translateX(-1px)", boxShadow: "0 0 12px 1px rgba(198,93,59,.45)" }} />
            <Handle bg="#fff" fg={INK} ring="rgba(198,93,59,.28)" />
          </Box>

          <Box title="H · Amber glow" note="Cream line with a warm amber glow — a touch brighter/golden than terracotta.">
            <div style={{ position: "absolute", top: 0, bottom: 0, left: POS, width: 2, background: "rgba(247,245,240,.95)", transform: "translateX(-1px)", boxShadow: "0 0 12px 1px rgba(224,144,43,.5)" }} />
            <Handle bg="#fff" fg={INK} ring="rgba(224,144,43,.3)" />
          </Box>

          <Box title="I · Cream halo (glow, no colour)" note="A soft neutral cream glow — luminous seam without any accent colour.">
            <div style={{ position: "absolute", top: 0, bottom: 0, left: POS, width: 2, background: "#fff", transform: "translateX(-1px)", boxShadow: "0 0 14px 2px rgba(247,245,240,.8)" }} />
            <Handle bg="#fff" fg={INK} />
          </Box>

          <Box title="J · Soft glow band" note="No hard line — a wide, feathered terracotta glow band marks the seam. Diffuse and modern.">
            <div style={{ position: "absolute", top: 0, bottom: 0, left: POS, width: 40, transform: "translateX(-20px)", background: "linear-gradient(90deg, rgba(198,93,59,0), rgba(198,93,59,.28), rgba(198,93,59,0))", pointerEvents: "none" }} />
            <Handle bg="linear-gradient(135deg,#211307,#2B1E15)" fg="#fff" ring="rgba(198,93,59,.3)" />
          </Box>

          <Box title="K · Handle glow only" note="Thin cream line with no glow; the glow lives only on the handle ring. Focused, tidy.">
            <div style={{ position: "absolute", top: 0, bottom: 0, left: POS, width: 1.5, background: "rgba(247,245,240,.9)", transform: "translateX(-0.75px)" }} />
            <div style={{ position: "absolute", top: "50%", left: POS, transform: "translate(-50%,-50%)", width: 34, height: 34, borderRadius: 999, background: "#fff", color: INK, display: "flex", alignItems: "center", justifyContent: "center", gap: 2, fontSize: 12, boxShadow: "0 0 16px 2px rgba(198,93,59,.55), 0 6px 18px -6px rgba(33,19,7,.5)" }}><span>&#8249;</span><span>&#8250;</span></div>
          </Box>

          <Box title="L · Gradient line + glow" note="A terracotta→amber gradient line with a soft matching glow. The richest option.">
            <div style={{ position: "absolute", top: 0, bottom: 0, left: POS, width: 2.5, background: "linear-gradient(180deg,#D1633D,#E0902B)", transform: "translateX(-1.25px)", boxShadow: "0 0 12px 1px rgba(198,93,59,.4)" }} />
            <Handle bg="#fff" fg={TERRA} ring="rgba(198,93,59,.28)" />
          </Box>

        </div>
      </div>
    </div>
  );
}

